import React from 'react';
import { ChevronDown, Check, XCircle } from 'lucide-react';

/**
 * Inline transporteur (shipping company) selector — table cell (Phase 13.5, updated 13.8).
 *
 * Props:
 *   value       — current shipping_company string (or null / '')
 *   options     — [{id, name}, …] active shipping companies (server already
 *                 filters `is_active=true` in `/api/orders/filters`).
 *   onChange(n) — called with the new name string OR null (to clear).
 *   disabled    — when true, renders inert text.
 *
 * PATCH is fired by the caller. On success, the caller emits a discrete
 * bottom-right 2 s toast (`toastStatusChange`). Cell updates immediately
 * via the caller's optimistic setState.
 *
 * Empty-cell UX (Phase 13.8): shows "Assigner" in muted grey to signal
 * clickability, instead of a bland "—".
 *
 * "Aucun transporteur" (Phase 13.8): neutral action separated from the
 * carrier options — grey text, no checkmark, separator above.
 */
export default function InlineShippingSelect({ value, options = [], onChange, disabled = false, testId }) {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef(null);

    React.useEffect(() => {
        function handler(e) {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const hasValue = !!value;

    if (disabled) {
        return <span className={hasValue ? 'text-[#1A1D2E] uppercase' : 'text-[#1A1D2E]'}>{hasValue ? value : '—'}</span>;
    }

    return (
        <div ref={rootRef} className="relative inline-block">
            <button
                type="button"
                data-testid={testId || 'inline-shipping-trigger'}
                data-empty={hasValue ? 'false' : 'true'}
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                className={`inline-flex items-center gap-1 h-7 px-2 rounded-md border border-[#E5E7EB] bg-white text-xs hover:bg-[#F5F6FA] transition-colors ${hasValue ? 'text-[#1A1D2E]' : 'text-[#9CA3AF] italic'}`}
            >
                <span className={hasValue ? 'uppercase' : ''}>{hasValue ? value : 'Assigner'}</span>
                <ChevronDown size={12} strokeWidth={2} className="text-[#6B7280]" />
            </button>

            {open && (
                <div
                    data-testid="inline-shipping-menu"
                    role="menu"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-0 z-30 mt-1 min-w-[180px] rounded-lg border border-[#E5E7EB] bg-white shadow-lg py-1 animate-fade-in-up"
                >
                    {options.map((opt) => (
                        <button
                            type="button"
                            key={opt.id || opt.name}
                            data-testid={`inline-shipping-option-${opt.name}`}
                            onClick={() => { setOpen(false); if (opt.name !== value) onChange(opt.name); }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[#1A1D2E] hover:bg-[#F5F6FA] text-left"
                        >
                            <span className="uppercase">{opt.name}</span>
                            {opt.name === value && <Check size={14} strokeWidth={2} className="ml-auto text-[#4B3FD8]" />}
                        </button>
                    ))}

                    {/* "Aucun transporteur" — neutral action, distinct from
                        carrier options: grey italic, separator above, no
                        checkmark even when the current value is empty. */}
                    {options.length > 0 && (
                        <div className="my-1 border-t border-[#F0F0F0]" />
                    )}
                    <button
                        type="button"
                        data-testid="inline-shipping-option-empty"
                        onClick={() => { setOpen(false); if (hasValue) onChange(null); }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[#6B7280] italic hover:bg-[#F5F6FA] text-left"
                    >
                        <XCircle size={13} strokeWidth={1.75} className="text-[#9CA3AF]" />
                        Aucun transporteur
                    </button>
                </div>
            )}
        </div>
    );
}
